import React, { useState } from 'react';
import { parseMessageWithMentions, ParsedMention } from '../lib/mention-utils';
import { PreviewDialog } from './preview-dialog';
import { FileSystemItem } from 'types';
import api from 'api/axios';

interface MessageWithMentionsProps {
  content: string;
  className?: string;
}

/**
 * Component that renders a message with mentions properly formatted
 * Mentions in the format @[display]{id} are converted to @display for display
 * Clicking on mentions opens a preview dialog
 */
export const MessageWithMentions: React.FC<MessageWithMentionsProps> = ({ 
  content, 
  className = "" 
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileSystemItem | null>(null);
  const [loading, setLoading] = useState(false);
  
  const parsedMessage = parseMessageWithMentions(content);
  
  // Handle mention click to open preview
  const handleMentionClick = async (mention: ParsedMention) => {
    if (loading) return;
    
    setLoading(true);
    try {
      // First, try to find the document in the Documents folder
      // This follows the same pattern as the thread component
      const foldersResp = await api.get("/folders");
      const rootItems = foldersResp.data as Array<{ id: string; name: string }>;
      const docsFolderId = rootItems.find((i) => i.name === "Documents")?.id;
      
      if (!docsFolderId) {
        throw new Error("Documents folder not found");
      }
      
      // Get all documents from the Documents folder
      const docsResp = await api.get(`/folders/${docsFolderId}/documents`);
      const documents = docsResp.data || [];
      
      // Find the specific document by ID
      const fileData = documents.find((doc: any) => doc.id === mention.id);
      
      if (!fileData) {
        throw new Error("Document not found");
      }
      
      // Create FileSystemItem object for preview
      const fileItem: FileSystemItem = {
        id: fileData.id,
        name: fileData.name || mention.name,
        created_at: fileData.created_at || "",
        updated_at: fileData.updated_at || "",
        agent_document_id: fileData.agent_document_id,
        parent_id: fileData.parent_id,
        workspace_id: fileData.workspace_id || "",
        path: fileData.path || "",
        document_metadata: {
          file_size: fileData.document_metadata?.file_size || 0,
          file_type: fileData.document_metadata?.file_type || "pdf",
          original_filename: fileData.document_metadata?.original_filename || fileData.name || mention.name,
          uploaded_at: fileData.document_metadata?.uploaded_at || fileData.created_at || "",
        },
        type: "Document",
      };
      
      setPreviewFile(fileItem);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Failed to fetch file metadata:', error);
      // You could show a toast notification here
    } finally {
      setLoading(false);
    }
  };
  
  // If no mentions, just return the content as is
  if (parsedMessage.mentions.length === 0) {
    return <span className={className}>{content}</span>;
  }
  
  // Create a more sophisticated approach to handle mentions with spaces
  let result = parsedMessage.displayText;
  
  // Replace each mention with a placeholder, then replace placeholders with styled components
  const mentionPlaceholders: { placeholder: string; mention: ParsedMention }[] = [];
  
  parsedMessage.mentions.forEach((mention, index) => {
    const placeholder = `__MENTION_PLACEHOLDER_${index}__`;
    mentionPlaceholders.push({ placeholder, mention });
    result = result.replace(`@${mention.name}`, placeholder);
  });
  
  // Split by placeholders to get text segments
  const parts = result.split(/(__MENTION_PLACEHOLDER_\d+__)/g);
  
  return (
    <>
      <span className={className}>
        {parts.map((part, index) => {
          // Check if this part is a placeholder
          const placeholderMatch = part.match(/__MENTION_PLACEHOLDER_(\d+)__/);
          if (placeholderMatch) {
            const mentionIndex = parseInt(placeholderMatch[1]);
            const { mention } = mentionPlaceholders[mentionIndex];
            
            return (
              <span 
                key={index}
                className={`bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-md font-medium transition-colors ${
                  loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800/50'
                }`}
                title={loading ? "Loading..." : `Click to preview: ${mention.name}`}
                onClick={() => !loading && handleMentionClick(mention)}
              >
                @{mention.name}
              </span>
            );
          }
          
          return part;
        })}
      </span>
      
      {/* Preview Dialog */}
      <PreviewDialog
        isOpen={previewOpen}
        setIsOpen={setPreviewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewFile(null);
        }}
        file={previewFile}
        page={1}
      />
    </>
  );
};
